import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';

import recordActions from '../../../actions/recordActions';
import apiActions from '../../../actions/apiActions';
import ganttActions from '../../../actions/ganttActions';
import getRecordsArgs from '../../../utils/getRecordsArgs';
import { REQUEST_FORMAT } from '../../../utils/formatDate';

const PAGE_SIZE = 1000; // записей за один запрос
const MAX_RECORDS = 10000; // весь бюджет выборки, половина в прошлое, половина в будущее
const AUTO_BUILD_LIMIT = 2000; // до стольких записей строим сразу, дальше спрашиваем

// Состояния загрузки по порядку
export const STATUS = {
  COUNTING: 'counting',
  CONFIRM: 'confirm',
  LOADING: 'loading',
  READY: 'ready',
};

/**
 * Загрузка записей диаграммы: выборка вокруг сегодня, половина бюджета в каждую сторону.
 *
 * Отдаёт статус и команду build — кнопка «Построить» запускает сборку вручную.
 */
const useGanttRecords = ({
  catalogId,
  sceneId,
  viewId,
  filters,
  records,
  recordsCount,
  shouldReload,
  startId,
  endId,
  titleId,
  employeeId,
  progressId,
  colorId,
  linkFieldId,
}) => {
  const selectionKey = useMemo(
    () =>
      [
        catalogId,
        viewId,
        startId,
        endId,
        titleId,
        employeeId,
        progressId,
        colorId,
        linkFieldId,
        filters && filters.hashCode(),
      ].join('|'),
    [catalogId, viewId, startId, endId, titleId, employeeId, progressId, colorId, linkFieldId, filters],
  );

  const [status, setStatus] = useState(null);

  // Согласие человека строить выборку, которая больше порога. Держится до тех пор, пока
  // выборка та же: обновление записей переспрашивать не должно, а смена фильтра — должна.
  const [confirmed, setConfirmed] = useState(false);

  const recordsRef = useRef(records);
  recordsRef.current = records;
  const countRecordsRef = useRef(records);

  const loadFields = useMemo(
    () => _.uniq([startId, endId, titleId, employeeId, progressId, colorId, linkFieldId].filter(Boolean)),
    [startId, endId, titleId, employeeId, progressId, colorId, linkFieldId],
  );

  const fetchCount = useCallback(() => {
    countRecordsRef.current = recordsRef.current;
    recordActions.requestForRecordsImmediate(
      catalogId,
      sceneId,
      { offset: 0, limit: 1, viewId, fields: [] },
      'gantt',
      filters,
    );
  }, [catalogId, sceneId, viewId, filters]);

  const fetchPage = useCallback(
    (forward, offset, limit) => {
      const today = moment().startOf('day').format(REQUEST_FORMAT);
      const request = {
        offset,
        limit,
        viewId,
        fields: loadFields,
        filters: [{ fieldId: startId, value: forward ? { at: today } : { to: today } }],
      };
      const args = getRecordsArgs._prepareGetRecordsArgs(
        catalogId,
        sceneId,
        request,
        'gantt',
        filters,
        { sortField: startId, sortType: forward ? 1 : -1 },
        {},
        false,
      );

      return apiActions.getRecords(...args).catch(() => []);
    },
    [catalogId, sceneId, viewId, filters, loadFields, startId],
  );

  const fetchSide = useCallback(
    async (forward, quota, cancelled, start = 0) => {
      const page = [];

      while (page.length < quota) {
        const limit = Math.min(PAGE_SIZE, quota - page.length);
        const chunk = await fetchPage(forward, start + page.length, limit);
        page.push(...chunk);

        if (chunk.length < limit || cancelled()) break;
      }

      return page;
    },
    [fetchPage],
  );

  useEffect(() => {
    if (!startId) return;

    setConfirmed(false);
    setStatus(STATUS.COUNTING);
    fetchCount();
  }, [selectionKey, startId, sceneId, fetchCount]);

  // Кнопка «Обновить записи» своего обработчика не зовёт, а поднимает флаг в сцене —
  // каждый вид обязан заметить его сам. Сам флаг снимется, когда уйдёт запрос счётчика
  useEffect(() => {
    if (!shouldReload || !startId) return;

    setStatus(STATUS.COUNTING);
    fetchCount();
  }, [shouldReload, startId, fetchCount]);

  useEffect(() => {
    if (status !== STATUS.COUNTING || recordsCount == null) return;
    if (records === countRecordsRef.current) return;

    const needConfirm = recordsCount > AUTO_BUILD_LIMIT && !confirmed;
    setStatus(needConfirm ? STATUS.CONFIRM : STATUS.LOADING);
  }, [records, recordsCount, status, confirmed]);

  useEffect(() => {
    if (status !== STATUS.LOADING) return undefined;

    let stopped = false;
    const cancelled = () => stopped;

    const load = async () => {
      const half = Math.floor(MAX_RECORDS / 2);
      const [future, past] = await Promise.all([fetchSide(true, half, cancelled), fetchSide(false, half, cancelled)]);
      if (stopped) return;

      let extra = [];
      if (future.length < half && past.length === half) {
        extra = await fetchSide(false, half - future.length, cancelled, past.length);
      } else if (past.length < half && future.length === half) {
        extra = await fetchSide(true, half - past.length, cancelled, future.length);
      }
      if (stopped) return;

      const startAt = (record) => new Date(_.get(record, ['values', startId]));
      const union = _.sortBy(_.uniqBy([...past, ...extra, ...future], 'id'), startAt);

      ganttActions.setGanttRecords(sceneId, union);
      setStatus(STATUS.READY);
    };

    load();

    return () => {
      stopped = true;
    };
  }, [status, fetchSide, sceneId, startId]);

  const build = useCallback(() => {
    setConfirmed(true);
    setStatus(STATUS.LOADING);
  }, []);

  return { status, build };
};

export default useGanttRecords;
